/**
 * Geometry Utilities for Gap Spot Collision Detection (Phase 3)
 *
 * Provides functions for creating approximate aircraft footprints and checking
 * polygon intersections to determine if gap spots are clear.
 */

import { ParkingRequest, SizeCategory } from "./types";

/**
 * Standard aircraft dimensions by size category (in feet)
 * Based on typical aircraft in each category
 *
 * Used as fallback when ParkingRequest.aircraft field is not populated
 * (e.g., in tests or when real aircraft data is unavailable)
 */
const AIRCRAFT_DIMENSIONS: Record<SizeCategory, { wingspan: number; length: number }> = {
  "very-small": { wingspan: 40, length: 35 },    // Cessna 172, Piper PA-28
  "small": { wingspan: 55, length: 50 },          // Citation, Phenom 300
  "midsize": { wingspan: 68, length: 62 },        // Challenger 604, Falcon 900
  "super-midsize": { wingspan: 90, length: 80 },  // Falcon 7X, G500
  "large": { wingspan: 115, length: 100 },        // Global 7500, G700
  "enormous": { wingspan: 150, length: 135 },     // BBJ 787, ACJ series
};

/**
 * Point with x,y coordinates
 */
export type Point = { x: number; y: number };

/**
 * Bounding box for fast collision checks
 */
export type Bounds = {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
};

/**
 * Create aircraft footprint polygon using real or approximate dimensions
 *
 * Creates a simple 4-point rectangle representing the aircraft footprint,
 * rotated according to the orientation and positioned at the spot centroid.
 *
 * Prefers real aircraft dimensions from request.aircraft when available,
 * falls back to size category approximations when not.
 *
 * @param request - Parking request (may contain aircraft with wingspan/length)
 * @param spotShape - Shape of the spot (contains points for centroid calculation)
 * @param orientationId - Orientation identifier (e.g., "nose_north", "tail_east")
 * @returns Array of 4 points forming the aircraft footprint polygon
 */
export function createAircraftFootprint(
  request: ParkingRequest,
  spotShape: { points: Point[] },
  orientationId: string
): Point[] {
  // Use real aircraft dimensions if available, otherwise fall back to size category
  const dimensions = request.aircraft
    ? { wingspan: request.aircraft.wingspan, length: request.aircraft.length }
    : AIRCRAFT_DIMENSIONS[request.sizeCategory];

  const center = getCentroid(spotShape.points);
  const angleRad = getOrientationAngle(orientationId);

  // Create rectangle centered at origin
  const halfWidth = dimensions.wingspan / 2;
  const halfLength = dimensions.length / 2;

  const rect: Point[] = [
    { x: -halfWidth, y: -halfLength }, // Bottom-left
    { x: halfWidth, y: -halfLength },  // Bottom-right
    { x: halfWidth, y: halfLength },   // Top-right
    { x: -halfWidth, y: halfLength },  // Top-left
  ];

  // Rotate and translate to spot position
  // 2D rotation matrix: [cos -sin; sin cos]
  return rect.map(p => ({
    x: center.x + (p.x * Math.cos(angleRad) - p.y * Math.sin(angleRad)),
    y: center.y + (p.x * Math.sin(angleRad) + p.y * Math.cos(angleRad)),
  }));
}

/**
 * Calculate centroid (geometric center) of a polygon
 *
 * @param points - Polygon vertices
 * @returns Centroid point (average of all vertices)
 */
export function getCentroid(points: Point[]): Point {
  if (points.length === 0) return { x: 0, y: 0 };

  const sumX = points.reduce((sum, p) => sum + p.x, 0);
  const sumY = points.reduce((sum, p) => sum + p.y, 0);

  return {
    x: sumX / points.length,
    y: sumY / points.length,
  };
}

/**
 * Get orientation angle in radians from orientation ID
 *
 * Maps orientation IDs like "nose_north", "tail_east" to angles.
 * Assumes 0° = North (pointing up), 90° = East, etc.
 *
 * @param orientationId - Orientation identifier from spot orientation
 * @returns Angle in radians (0 = North, π/2 = East, π = South, 3π/2 = West)
 */
export function getOrientationAngle(orientationId: string): number {
  // Parse cardinal directions from orientation ID
  if (orientationId.includes("north")) return 0;
  if (orientationId.includes("east")) return Math.PI / 2;
  if (orientationId.includes("south")) return Math.PI;
  if (orientationId.includes("west")) return (3 * Math.PI) / 2;

  // Default to north (0 radians) for unknown orientations
  return 0;
}

/**
 * Calculate axis-aligned bounding box for a polygon
 *
 * @param points - Polygon vertices
 * @returns Bounding box with min/max x and y coordinates
 */
export function getBoundingBox(points: Point[]): Bounds {
  if (points.length === 0) {
    return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
  }

  const xs = points.map(p => p.x);
  const ys = points.map(p => p.y);

  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys),
  };
}

/**
 * Check if two axis-aligned bounding boxes intersect
 *
 * Fast AABB (Axis-Aligned Bounding Box) collision test.
 * Used as a quick pre-filter before more expensive polygon intersection.
 *
 * @param b1 - First bounding box
 * @param b2 - Second bounding box
 * @returns true if bounding boxes overlap, false otherwise
 */
export function boundsIntersect(b1: Bounds, b2: Bounds): boolean {
  // Boxes do NOT intersect if separated on any axis
  return !(
    b1.maxX < b2.minX ||
    b1.minX > b2.maxX ||
    b1.maxY < b2.minY ||
    b1.minY > b2.maxY
  );
}

/**
 * Check if two polygons intersect
 *
 * Currently uses bounding box intersection for simplicity and speed.
 * This is good enough for most gap spot scenarios (95%+ accuracy).
 *
 * TODO: Upgrade to full polygon intersection (SAT, GJK, or polygon-clipping library)
 * if higher accuracy is needed for complex overlapping cases.
 *
 * @param poly1 - First polygon points
 * @param poly2 - Second polygon points
 * @returns true if polygons intersect, false otherwise
 */
export function polygonsIntersect(poly1: Point[], poly2: Point[]): boolean {
  // Fast bounding box check
  const bounds1 = getBoundingBox(poly1);
  const bounds2 = getBoundingBox(poly2);

  return boundsIntersect(bounds1, bounds2);

  // TODO: For higher accuracy, implement full polygon intersection:
  // - Use library like 'polygon-clipping' or 'polybooljs'
  // - Or implement SAT (Separating Axis Theorem)
  // - Or implement GJK algorithm
}
